import { Edge, Node } from "@xyflow/react";
import { MutableRefObject } from "react";
import {
    ACTION, EDGE, GHOST_EDGE_PREFIX, GHOST_NODE_PREFIX,
} from "./constants";

export interface InjectGhostProps {
    currentNodes: Node[];
    currentEdges: Edge[];
    ghostParentId: string;
    insertIndex: number;
    lastGhostParentIdRef: MutableRefObject<string | undefined>;
    lastGhostInsertIndexRef: MutableRefObject<number>;
}

export function injectGhost({
    currentNodes,
    currentEdges,
    ghostParentId,
    insertIndex,
    lastGhostParentIdRef,
    lastGhostInsertIndexRef,
}: InjectGhostProps): { nodes: Node[]; edges: Edge[] } {
    // Start from clean lists (strip any previous ghost)
    const filteredNodes = currentNodes.filter(node => !node.id.startsWith(GHOST_NODE_PREFIX));
    const filteredEdges = currentEdges.filter(edge => !edge.id.startsWith(GHOST_EDGE_PREFIX));

    const ghostNodeId = `${GHOST_NODE_PREFIX}${ghostParentId}`;
    const ghostNode: Node = {
        id: ghostNodeId,
        type: ACTION.GHOST,
        position: { x: 0, y: 0 },
        data: { id: ghostNodeId, parentId: ghostParentId },
    } as Node;

    let absoluteInsertIndex = lastGhostInsertIndexRef.current;

    if (ghostParentId !== lastGhostParentIdRef.current) {
        const siblingIndexes = filteredNodes
            .map((nodeItem, idx) => ({ nodeItem, idx }))
            .filter(listItem => listItem.nodeItem.data?.parentId === ghostParentId)
            .map(listItem => listItem.idx)
            .sort((index1, index2) => index1 - index2);

        absoluteInsertIndex = -1;

        if (insertIndex === siblingIndexes.length) {
            if (siblingIndexes.length === 0) {
                const parentPosition = filteredNodes.findIndex(nodeItem => nodeItem.id === ghostParentId);
                absoluteInsertIndex = parentPosition !== -1 ? parentPosition + 1 : filteredNodes.length;
            } else {
                absoluteInsertIndex = Math.max(...siblingIndexes) + 1;
            }
        } else if (insertIndex < siblingIndexes.length) {
            absoluteInsertIndex = siblingIndexes[insertIndex];
        }

        if (absoluteInsertIndex === -1) {
            absoluteInsertIndex = siblingIndexes.length > 0
                ? Math.max(...siblingIndexes) + 1
                : filteredNodes.findIndex(nodeItem => nodeItem.id === ghostParentId) + 1;
        }

        if (absoluteInsertIndex < 0 || absoluteInsertIndex > filteredNodes.length) {
            absoluteInsertIndex = filteredNodes.length;
        }

        /* eslint-disable no-param-reassign */
        lastGhostParentIdRef.current = ghostParentId;
        lastGhostInsertIndexRef.current = absoluteInsertIndex;
    } else if (absoluteInsertIndex < 0 || absoluteInsertIndex > filteredNodes.length) {
        absoluteInsertIndex = Math.min(insertIndex, filteredNodes.length);
        /* eslint-disable no-param-reassign */
        lastGhostInsertIndexRef.current = absoluteInsertIndex;
    }

    if (!filteredNodes.some(nodeItem => nodeItem.id === ghostNodeId)) {
        filteredNodes.splice(absoluteInsertIndex, 0, ghostNode);
    }

    const edgeId = `${GHOST_EDGE_PREFIX}${ghostParentId}`;
    if (!filteredEdges.some(edgeItem => edgeItem.id === edgeId)) {
        filteredEdges.push({
            id: edgeId,
            source: ghostParentId,
            target: ghostNodeId,
            type: EDGE.CURVED,
            data: { sourceId: ghostParentId, targetId: ghostNodeId },
        });
    }

    return { nodes: filteredNodes, edges: filteredEdges };
}
